import {
  AppBar,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  SvgIcon,
  Tab,
  Tabs,
  Toolbar,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useMutation } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { SyntheticEvent, useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { getFeaturePermission } from '@zeta-commons';
import { enqueueSnackbar, RouterLink, useAppbarStore, useOffSetTop, useResponsive, useUserAccessStore } from '@zeta-core';
import { MinimiseIcon, ZetaAiIcon, ZetaSvgLogo } from '@zeta-core/assets';
import { getErrorMessage, NotificationService } from '@zeta-services';
import { IGenericErrorResponse, INotificationAppViewResponse, INotificationsAppViewRequest } from '@zeta-types';
import { RouterLinks } from '../../app-route-paths';
import { DASHBOARD_CONFIG } from '../common/constants';
import { ILinkType } from '../common/types';
import AccountPopover from './account-popover';
import NotificationsPopover from './notifications-popover';
interface Props {
  navData?: ILinkType[];
  noData?: boolean;
  hideNavOptions?: boolean;
}
const Header = ({ navData, noData, hideNavOptions }: Props) => {
  const userAccess = useUserAccessStore((state) => state.userAccess);
  const theme = useTheme();
  const navigate = useNavigate();
  const lgUp = useResponsive('up', 'lg');
  const { pathname } = useLocation();
  const offset = useOffSetTop(DASHBOARD_CONFIG.HEADER.DESKTOP_HEIGHT);
  const updateAppbar = useAppbarStore((state) => state?.updateAppbarStore);

  const [notificationsOverview, setNotificationsOverview] = useState<INotificationAppViewResponse | null>(null);
  const headerBg =
    theme.palette.mode === 'dark'
      ? `linear-gradient(180deg, ${alpha(theme.palette.background.paper, 0.98)} 0%, ${alpha(theme.palette.background.default, 1)} 100%)`
      : theme.palette.background.paper;

  const linkColor = (active: boolean) => {
    let color;

    if (active) {
      color = theme.palette.primary.light;
    } else if (theme.palette.mode === 'dark') {
      color = theme.palette.text.primary;
    } else {
      color = theme.palette.text.primary;
    }

    return color;
  };

  const TABS = [
    {
      value: 'all',
      label: 'All',
      count: notificationsOverview?.appCountInfo?.totalCount || 0,
    },
    {
      value: 'read',
      label: 'Read',
      count: notificationsOverview?.appCountInfo?.readCount || 0,
    },
    {
      value: 'unread',
      label: 'Unread',
      count: notificationsOverview?.appCountInfo?.unreadCount || 0,
    },
  ];

  const [currentTab, setCurrentTab] = useState('all');
  const [markedAsRead, setMarkedAsRead] = useState(false);
  const notificationPermission = getFeaturePermission(userAccess?.access, 'AdvisorNotification');

  const handleChangeTab = useCallback((event: SyntheticEvent, newValue: string) => {
    setCurrentTab(newValue);
  }, []);

  const renderTabs = useCallback(
    () => (
      <Tabs
        value={currentTab}
        onChange={handleChangeTab}
        variant="fullWidth"
        sx={{
          width: '100%',
          '& .MuiTabs-indicator': {
            backgroundColor: 'transparent',
          },
          '& .Mui-selected': {
            background: theme.palette.primary.contrastText,
            margin: '10px',
            borderRadius: '8px',
            boxShadow: '0px 1px 2px 0px #919EAB29',
            color: theme.palette.primary.main,
          },
        }}
      >
        {TABS.map((tab) => (
          <Tab key={tab.value} iconPosition="end" value={tab.value} label={tab.label} />
        ))}
      </Tabs>
    ),
    [currentTab, handleChangeTab]
  );

  const notificationAppViewAPI = useMutation({
    mutationFn: (payload: INotificationsAppViewRequest) => NotificationService().notificationAppView(payload),

    onSuccess: (response: AxiosResponse<INotificationAppViewResponse>) => {
      const { data: responseData } = response;

      setNotificationsOverview(responseData);
    },
    onError: (error: IGenericErrorResponse) => {
      const errorCode = (error as any)?.response?.data?.errorCode || error?.response?.data?.error?.errorCode || 'INTERNAL_SERVER_ERROR';
      enqueueSnackbar(getErrorMessage(errorCode), { variant: 'error' });
    },
  });

  const payload: INotificationsAppViewRequest = {
    status: [],
    desc: true,
    skipPagination: true,
    isTotalCountRequired: true,
  };

  const fetchData = () => {
    notificationAppViewAPI.mutate(payload);
  };

  useEffect(() => {
    if (location.pathname.includes('auth')) {
      return; // Do not call the API if pathname includes 'auth'
    }

    fetchData();
    const intervalId = setInterval(fetchData, 300 * 1000);

    return () => clearInterval(intervalId);
  }, [location.pathname]); // Add location.pathname as a dependency to track changes

  useEffect(() => {
    if (markedAsRead) {
      fetchData();
      setMarkedAsRead(false);
    }
  }, [markedAsRead]);

  const displaySidebarIconPaths =
    location.pathname.includes(RouterLinks.clientDetailsAI) ||
    location.pathname.includes('para-planner') ||
    location.pathname.includes('managed-client') ||
    location.pathname.includes(RouterLinks.askZeta) ||
    location.pathname.includes(RouterLinks.clientInfo);

  return (
    <AppBar
      sx={{
        height: DASHBOARD_CONFIG.HEADER.DESKTOP_HEIGHT,
        zIndex: theme.zIndex.appBar + 1,
        background: headerBg,
        color: theme.palette.text.primary,
        boxShadow: offset ? theme.shadows[19] : 'none',
        borderBottom: `1px solid ${alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.12 : 0.3)}`,
      }}
    >
      <Toolbar
        disableGutters
        sx={{
          height: '100%',
          px: { lg: 5 },
        }}
      >
        {lgUp ? null : (
          <IconButton
            sx={{
              ml: 2,
              color: theme.palette.text.primary,
              display: displaySidebarIconPaths ? 'block' : 'none',
            }}
            onClick={() => updateAppbar({ sidebarOpen: true })}
          >
            <MinimiseIcon />
          </IconButton>
        )}
        <Stack>{lgUp ? <ZetaSvgLogo /> : null}</Stack>

        <List component={Stack} direction="row" justifyContent="center" width="100%" gap={4} disablePadding>
          {!(noData || hideNavOptions) &&
            navData?.map((item) => {
              if (!item.show) return null; // Skip rendering if the item.show is false

              const active = pathname.includes(item.path);
              return (
                <ListItem disableGutters disablePadding sx={{ width: 'max-content' }} key={item.path}>
                  <ListItemButton
                    id={item.id}
                    LinkComponent={RouterLink}
                    sx={{
                      color: linkColor(active),
                      '&:hover': { bgcolor: 'transparent !important' },
                    }}
                    disableGutters
                    disableRipple
                    disableTouchRipple
                    href={item.path}
                  >
                    <ListItemIcon>
                      <SvgIcon sx={{ height: 16, width: 16 }}>
                        <item.icon />
                      </SvgIcon>
                    </ListItemIcon>
                    <ListItemText primary={<Typography variant="body2">{item.title}</Typography>} />
                  </ListItemButton>
                </ListItem>
              );
            })}
        </List>

        {lgUp && !noData && location.pathname !== RouterLinks.firmProfileIncomplete ? (
          <NotificationsPopover
            renderTabs={renderTabs}
            notificationsCount={notificationsOverview?.appCountInfo?.unreadCount}
            currentTab={currentTab}
            setMarkedAsRead={setMarkedAsRead}
            permissions={notificationPermission}
          />
        ) : null}

        {lgUp && !noData && getFeaturePermission(userAccess?.access, 'DocumentAssistant').accessible ? (
          <Tooltip title="Zeta AI">
            <IconButton
              sx={{
                borderRadius: 3,
                boxShadow: theme.customShadows.primary,
                background: theme.palette.mode === 'dark' ? alpha(theme.palette.background.paper, 0.98) : alpha(theme.palette.background.paper, 0.9),
                border: `1px solid ${alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.12 : 0.28)}`,
                mx: 1,
                p: 0.35,
              }}
              onClick={() => navigate(RouterLinks.askZeta, { state: { source: 'agentic-chat' } })}
            >
              <ZetaAiIcon />
            </IconButton>
          </Tooltip>
        ) : null}

        {lgUp && !noData ? <AccountPopover /> : null}
      </Toolbar>
    </AppBar>
  );
};

export default Header;
